import React from 'react';
import { Shield, Scale, Users as CommunityIcon, Target, BookOpen, Flag } from 'lucide-react';
export function EmpoweringSection() {
  return <section className="py-16 md:py-24 bg-brand-gray-900 border-y border-brand-gray-800" data-id="element-236">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-237">
        <div className="max-w-4xl mx-auto text-center mb-16" data-id="element-238">
          <h2 className="text-3xl font-bold text-brand-white uppercase tracking-wide mb-6" data-id="element-239">
            Empowering Civilians Through Education and Responsibility
          </h2>
          <p className="text-xl text-brand-gray-300 leading-relaxed mb-6" data-id="element-240">
            We believe in the vital importance of equipping civilians with the
            tools, knowledge, and training necessary for self-defense while
            fostering a culture of responsibility and preparedness.
          </p>
          <p className="text-lg text-brand-gray-400 leading-relaxed" data-id="element-241">
            Civilian self-defense and concealed carry are essential elements of
            personal safety, empowerment, and the broader preservation of
            individual rights. Below are key reasons why these practices are
            crucial:
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8" data-id="element-242">
          <div className="bg-brand-black p-8 border border-brand-gray-800 h-full" data-id="element-243">
            <Shield className="w-8 h-8 text-brand-teal mb-6" data-id="element-244" />
            <h3 className="text-xl font-bold text-brand-white uppercase tracking-wide mb-4" data-id="element-245">
              Personal Protection
            </h3>
            <ul className="space-y-4" data-id="element-246">
              <li className="text-brand-gray-300 leading-relaxed" data-id="element-247">
                <strong className="text-brand-white" data-id="element-248">
                  Immediate Response:
                </strong>{' '}
                In emergencies where law enforcement may not be immediately
                available, concealed carry enables civilians to protect
                themselves and others against threats such as robbery, assault,
                or active shooter scenarios.
              </li>
              <li className="text-brand-gray-300 leading-relaxed" data-id="element-249">
                <strong className="text-brand-white" data-id="element-250">Crime Deterrence:</strong>{' '}
                The potential presence of an armed civilian can discourage
                criminals from committing acts of violence or aggression.
              </li>
            </ul>
          </div>

          <div className="bg-brand-black p-8 border border-brand-gray-800 h-full" data-id="element-251">
            <Scale className="w-8 h-8 text-brand-teal mb-6" data-id="element-252" />
            <h3 className="text-xl font-bold text-brand-white uppercase tracking-wide mb-4" data-id="element-253">
              Individual Rights and Freedoms
            </h3>
            <ul className="space-y-4" data-id="element-254">
              <li className="text-brand-gray-300 leading-relaxed" data-id="element-255">
                <strong className="text-brand-white" data-id="element-256">Second Amendment:</strong>{' '}
                In the United States, the constitutional right to bear arms
                affirms self-defense as a fundamental liberty.
              </li>
              <li className="text-brand-gray-300 leading-relaxed" data-id="element-257">
                <strong className="text-brand-white" data-id="element-258">Empowerment:</strong>{' '}
                Carrying a concealed firearm empowers individuals to take
                responsibility for their own safety, reducing dependence on
                external authorities.
              </li>
            </ul>
          </div>

          <div className="bg-brand-black p-8 border border-brand-gray-800 h-full" data-id="element-259">
            <CommunityIcon className="w-8 h-8 text-brand-teal mb-6" data-id="element-260" />
            <h3 className="text-xl font-bold text-brand-white uppercase tracking-wide mb-4" data-id="element-261">
              Public Safety
            </h3>
            <ul className="space-y-4" data-id="element-262">
              <li className="text-brand-gray-300 leading-relaxed" data-id="element-263">
                <strong className="text-brand-white" data-id="element-264">
                  A Supplement to Law Enforcement:
                </strong>{' '}
                Responsibly armed civilians can intervene in critical moments,
                potentially saving lives before authorities arrive.
              </li>
              <li className="text-brand-gray-300 leading-relaxed" data-id="element-265">
                <strong className="text-brand-white" data-id="element-266">Community Defense:</strong>{' '}
                In rural or remote areas, where law enforcement response times
                are extended, armed civilians often play an essential role in
                ensuring safety.
              </li>
            </ul>
          </div>

          <div className="bg-brand-black p-8 border border-brand-gray-800 h-full" data-id="element-267">
            <Target className="w-8 h-8 text-brand-teal mb-6" data-id="element-268" />
            <h3 className="text-xl font-bold text-brand-white uppercase tracking-wide mb-4" data-id="element-269">
              Equalizing Factor
            </h3>
            <ul className="space-y-4" data-id="element-270">
              <li className="text-brand-gray-300 leading-relaxed" data-id="element-271">
                <strong className="text-brand-white" data-id="element-272">
                  Against Physical Disparities:
                </strong>{' '}
                Firearms serve as an equalizer, empowering individuals who may
                be physically weaker, including women, the elderly, and those
                with disabilities.
              </li>
              <li className="text-brand-gray-300 leading-relaxed" data-id="element-273">
                <strong className="text-brand-white" data-id="element-274">
                  Against Armed Threats:
                </strong>{' '}
                Concealed carry ensures that law-abiding citizens can match the
                capabilities of armed criminals.
              </li>
            </ul>
          </div>

          <div className="bg-brand-black p-8 border border-brand-gray-800 h-full" data-id="element-275">
            <BookOpen className="w-8 h-8 text-brand-teal mb-6" data-id="element-276" />
            <h3 className="text-xl font-bold text-brand-white uppercase tracking-wide mb-4" data-id="element-277">
              Responsibility and Preparedness
            </h3>
            <p className="text-brand-gray-400 mb-4 italic" data-id="element-278">
              Concealed carry is a significant responsibility that requires:
            </p>
            <ul className="space-y-4" data-id="element-279">
              <li className="text-brand-gray-300 leading-relaxed" data-id="element-280">
                <strong className="text-brand-white" data-id="element-281">Proper Training:</strong>{' '}
                Civilians must develop proficiency and situational awareness to
                ensure safe and effective firearm use.
              </li>
              <li className="text-brand-gray-300 leading-relaxed" data-id="element-282">
                <strong className="text-brand-white" data-id="element-283">Legal Compliance:</strong>{' '}
                Adherence to regulations is essential to maintain safety and
                accountability.
              </li>
              <li className="text-brand-gray-300 leading-relaxed" data-id="element-284">
                <strong className="text-brand-white" data-id="element-285">Vigilance:</strong>{' '}
                Carrying a firearm fosters heightened awareness and a commitment
                to public safety.
              </li>
            </ul>
          </div>

          <div className="bg-brand-black p-8 border border-brand-gray-800 h-full" data-id="element-286">
            <Flag className="w-8 h-8 text-brand-teal mb-6" data-id="element-287" />
            <h3 className="text-xl font-bold text-brand-white uppercase tracking-wide mb-4" data-id="element-288">
              Preserving Rights and Values
            </h3>
            <div className="space-y-4" data-id="element-289">
              <p className="text-brand-gray-300 leading-relaxed" data-id="element-290">
                Civilian self-defense reflects broader constitutional and human
                rights, ensuring individuals retain the ability to defend their
                lives and freedoms. It also embodies cultural values of
                self-reliance and individual liberty.
              </p>
              <p className="text-brand-gray-300 leading-relaxed" data-id="element-291">
                While civilian self-defense is a powerful tool, it carries the
                weight of responsibility. Through proper education, rigorous
                training, and a deep understanding of safety protocols, we
                empower individuals to protect themselves and their communities,
                creating a culture of preparedness and accountability.
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>;
}